import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { 
  RefreshCw, 
  ThumbsUp, 
  ThumbsDown, 
  ExternalLink, 
  Newspaper, 
  Search,
  Filter,
  Clock,
  Zap,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  PenTool,
  Calendar,
  Moon,
  Sun,
  ListTodo,
  Play,
  Trash2,
  Settings
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs par média
const MEDIA_COLORS = {
  'EconomieMatin.fr': 'bg-blue-500/20 border-blue-500/40 text-blue-400',
  'PolitiqueMatin.fr': 'bg-red-500/20 border-red-500/40 text-red-400',
  'Armees.com': 'bg-green-500/20 border-green-500/40 text-green-400',
  'Santematin.fr': 'bg-pink-500/20 border-pink-500/40 text-pink-400',
  'LAutomobiliste.fr': 'bg-orange-500/20 border-orange-500/40 text-orange-400',
  'Lenergeek.com': 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400',
  'Greenetvert.fr': 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400',
  'socialmag.news': 'bg-purple-500/20 border-purple-500/40 text-purple-400',
  'Juriguide.com': 'bg-indigo-500/20 border-indigo-500/40 text-indigo-400',
  'Infomatin.fr': 'bg-sky-500/20 border-sky-500/40 text-sky-400',
  'AfriqueMatin.info': 'bg-amber-500/20 border-amber-500/40 text-amber-400',
};

// Mapping média → site WordPress
const MEDIA_TO_SITE = {
  'EconomieMatin.fr': 'economiematin',
  'PolitiqueMatin.fr': 'politiquematin',
  'Armees.com': 'armees',
  'Santematin.fr': 'santematin',
  'LAutomobiliste.fr': 'automobiliste',
  'Lenergeek.com': 'lenergeek',
};

const ScoutMedia = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [topicsByMedia, setTopicsByMedia] = useState({});
  const [permissions, setPermissions] = useState({ can_view: true, can_send_feedback: false });
  const [healthStatus, setHealthStatus] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Sites WordPress
  const [sites, setSites] = useState([]);
  
  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState('all');
  const [expandedMedia, setExpandedMedia] = useState({});
  
  // Sélection de sujets
  const [selectedTopics, setSelectedTopics] = useState({}); // { topicId: { topic, siteIds: [] } }
  const [showSiteSelector, setShowSiteSelector] = useState(null); // topicId ou null
  
  // File d'attente
  const [queue, setQueue] = useState([]);
  const [queueStats, setQueueStats] = useState(null);
  const [showQueue, setShowQueue] = useState(false);
  const [queueSettings, setQueueSettings] = useState({
    night_mode_enabled: false,
    weekend_mode_enabled: false,
    auto_process_enabled: false
  });
  
  // Feedbacks locaux
  const [localFeedbacks, setLocalFeedbacks] = useState({});
  
  // Mode de publication sélectionné
  const [publishMode, setPublishMode] = useState('manual'); // 'manual', 'night', 'weekend'

  // Fonctions de chargement converties en useCallback pour stabilité
  const loadPermissions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/scout/permissions`);
      setPermissions(response.data);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/wordpress-sites`);
      setSites(response.data);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  }, []);

  const checkHealth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/scout/health`);
      setHealthStatus(response.data);
    } catch (error) {
      setHealthStatus({ status: 'error', error: error.message });
    }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const [queueRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/scout/queue`),
        axios.get(`${API}/api/scout/queue/stats`)
      ]);
      
      setQueue(queueRes.data.items || []);
      setQueueStats(statsRes.data.stats);
      setQueueSettings(statsRes.data.settings || {});
    } catch (error) {
      console.error('Error loading queue:', error);
    }
  }, []);

  useEffect(() => {
    loadPermissions();
    checkHealth();
    loadSites();
    loadQueue();
  }, [loadPermissions, checkHealth, loadSites, loadQueue]);

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '20',
        exclude_seen: 'true'
      });
      
      if (selectedMedia !== 'all') {
        params.append('media', selectedMedia);
      }
      
      const response = await axios.get(`${API}/api/scout/topics?${params}`);
      
      if (response.data.success) {
        setTopics(response.data.topics || []);
        setTopicsByMedia(response.data.topics_by_media || {});
        setTotalCount(response.data.count || 0);
        setLastFetch(new Date().toISOString());
        setPermissions(prev => ({
          ...prev,
          can_send_feedback: response.data.can_send_feedback
        }));
        
        // Expand all media sections by default
        const expanded = {};
        Object.keys(response.data.topics_by_media || {}).forEach(media => {
          expanded[media] = true;
        });
        setExpandedMedia(expanded);
        
        toast.success(`${response.data.count} sujets chargés`);
      } else {
        toast.error(response.data.error || 'Erreur de chargement');
      }
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast.error('Erreur de connexion à ScoutMediaAI');
    } finally {
      setLoading(false);
    }
  }, [selectedMedia]);

  const sendFeedback = async (topicId, isUseful) => {
    if (!permissions.can_send_feedback) {
      toast.error('Fonction réservée aux administrateurs');
      return;
    }
    
    setLocalFeedbacks(prev => ({ ...prev, [topicId]: isUseful }));
    
    try {
      const response = await axios.post(`${API}/api/scout/feedback`, {
        topic_id: topicId,
        is_useful: isUseful
      });
      
      if (response.data.success) {
        toast.success(isUseful ? 'Marqué comme utile' : 'Marqué comme non utile');
      } else {
        setLocalFeedbacks(prev => {
          const updated = { ...prev };
          delete updated[topicId];
          return updated;
        });
        toast.error(response.data.error || 'Erreur');
      }
    } catch (error) {
      setLocalFeedbacks(prev => {
        const updated = { ...prev };
        delete updated[topicId];
        return updated;
      });
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Sélection d'un sujet
  const toggleTopicSelection = (topic, siteId = null) => {
    const topicId = topic.id;
    
    setSelectedTopics(prev => {
      const current = prev[topicId];
      
      if (!current) {
        // Nouveau sujet sélectionné
        const defaultSiteId = findMatchingSite(topic.media_domain);
        return {
          ...prev,
          [topicId]: {
            topic,
            siteIds: siteId ? [siteId] : (defaultSiteId ? [defaultSiteId] : [])
          }
        };
      }
      
      if (siteId) {
        // Toggle un site spécifique
        const newSiteIds = current.siteIds.includes(siteId)
          ? current.siteIds.filter(id => id !== siteId)
          : [...current.siteIds, siteId];
        
        if (newSiteIds.length === 0) {
          // Plus aucun site : désélectionner le sujet
          const updated = { ...prev };
          delete updated[topicId];
          return updated;
        }
        
        return {
          ...prev,
          [topicId]: { ...current, siteIds: newSiteIds }
        };
      } else {
        // Toggle le sujet entier
        const updated = { ...prev };
        delete updated[topicId];
        return updated;
      }
    });
  };

  const findMatchingSite = (mediaDomain) => {
    const siteKeyword = MEDIA_TO_SITE[mediaDomain];
    if (!siteKeyword) return null;
    
    const site = sites.find(s => 
      s.name.toLowerCase().includes(siteKeyword) ||
      s.url.toLowerCase().includes(siteKeyword)
    );
    return site?.id || null;
  };

  // Ajouter les sujets sélectionnés à la file d'attente
  const addSelectedToQueue = async () => {
    const selectedList = Object.values(selectedTopics);
    if (selectedList.length === 0) {
      toast.error('Aucun sujet sélectionné');
      return;
    }
    
    // Vérifier que tous ont au moins un site
    const invalid = selectedList.filter(s => s.siteIds.length === 0);
    if (invalid.length > 0) {
      toast.error(`${invalid.length} sujet(s) sans site assigné`);
      return;
    }
    
    setLoading(true);
    try {
      let totalAdded = 0;
      
      for (const { topic, siteIds } of selectedList) {
        const response = await axios.post(`${API}/api/scout/queue/add`, {
          topic,
          site_ids: siteIds,
          mode: publishMode
        });
        
        if (response.data.success) {
          totalAdded += response.data.items_added;
        }
      }
      
      toast.success(`${totalAdded} article(s) ajouté(s) à la file d'attente`);
      setSelectedTopics({});
      loadQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const removeFromQueue = async (itemId) => {
    try {
      await axios.delete(`${API}/api/scout/queue/${itemId}`);
      toast.success('Retiré de la file');
      loadQueue();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateSettings = async (key, value) => {
    try {
      await axios.post(`${API}/api/scout/queue/settings`, {
        [key]: value
      });
      setQueueSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Paramètre mis à jour');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filterTopics = (topicsList) => {
    if (!searchQuery.trim()) return topicsList;
    
    const query = searchQuery.toLowerCase();
    return topicsList.filter(topic => 
      topic.title?.toLowerCase().includes(query) ||
      topic.source?.toLowerCase().includes(query) ||
      (topic.keywords || []).some(k => k.toLowerCase().includes(query))
    );
  };

  const toggleMediaSection = (media) => {
    setExpandedMedia(prev => ({
      ...prev,
      [media]: !prev[media]
    }));
  };

  const getMediaColor = (media) => {
    return MEDIA_COLORS[media] || 'bg-slate-500/20 border-slate-500/40 text-slate-400';
  };

  const formatScheduledTime = (isoString) => {
    if (!isoString) return 'Non planifié';
    const date = new Date(isoString);
    return date.toLocaleString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const allMedias = Object.keys(topicsByMedia).sort();
  const selectedCount = Object.keys(selectedTopics).length;
  const totalSelectedArticles = Object.values(selectedTopics).reduce((sum, s) => sum + s.siteIds.length, 0);

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="scout-media-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold font-mono tracking-widest uppercase text-sky-400 mb-2"
              style={{ textShadow: '0 0 30px rgba(0,229,255,0.3)' }}>
            SCOUT_MEDIA_AI
          </h1>
          <p className="text-slate-400">
            Sélectionnez les sujets à publier automatiquement
            {permissions.can_send_feedback && (
              <Badge variant="outline" className="ml-2 text-green-400 border-green-400/50">
                Mode admin (TEST)
              </Badge>
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne principale - Sujets */}
          <div className="lg:col-span-2 space-y-4">
            {/* Status & Actions */}
            <Card className="bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {healthStatus?.status === 'connected' ? (
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-sm font-mono">API OK</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-400">
                        <XCircle className="w-5 h-5" />
                        <span className="text-sm font-mono">API Erreur</span>
                      </div>
                    )}
                    
                    <Badge variant="secondary" className="font-mono">
                      {totalCount} sujets
                    </Badge>
                  </div>

                  <Button
                    onClick={fetchTopics}
                    disabled={loading}
                    className="bg-sky-600 hover:bg-sky-500"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Actualiser
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Filtres */}
            <Card className="bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Rechercher..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-slate-900/50 border-slate-700"
                      />
                    </div>
                  </div>

                  <select
                    value={selectedMedia}
                    onChange={(e) => setSelectedMedia(e.target.value)}
                    className="bg-slate-900/50 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200"
                  >
                    <option value="all">Tous les médias</option>
                    {allMedias.map(media => (
                      <option key={media} value={media}>{media}</option>
                    ))}
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Liste des sujets par média */}
            {topics.length === 0 && !loading ? (
              <Card className="bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
                <CardContent className="p-8 text-center">
                  <Newspaper className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                  <p className="text-slate-400 mb-4">
                    Cliquez sur &quot;Actualiser&quot; pour charger les sujets
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {allMedias.map(media => {
                  const mediaTopics = filterTopics(topicsByMedia[media] || []);
                  if (mediaTopics.length === 0) return null;
                  
                  return (
                    <Card key={media} className="bg-slate-950/40 backdrop-blur-xl border-slate-800/60 overflow-hidden">
                      <div 
                        className={`p-4 cursor-pointer hover:bg-slate-800/30 transition-colors border-b border-slate-800/60 ${getMediaColor(media)}`}
                        onClick={() => toggleMediaSection(media)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Newspaper className="w-5 h-5" />
                            <span className="font-bold font-mono">{media}</span>
                            <Badge variant="secondary" className="text-xs">
                              {mediaTopics.length} sujets
                            </Badge>
                          </div>
                          {expandedMedia[media] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>

                      {expandedMedia[media] && (
                        <div className="divide-y divide-slate-800/60">
                          {mediaTopics.map((topic, index) => {
                            const isSelected = !!selectedTopics[topic.id];
                            const selectedSiteIds = selectedTopics[topic.id]?.siteIds || [];
                            
                            return (
                              <div 
                                key={topic.id || index}
                                className={`p-4 transition-colors ${isSelected ? 'bg-sky-500/10' : 'hover:bg-slate-800/20'}`}
                              >
                                <div className="flex items-start gap-4">
                                  {/* Checkbox de sélection */}
                                  {permissions.can_send_feedback && (
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={() => toggleTopicSelection(topic)}
                                      className="mt-1"
                                    />
                                  )}
                                  
                                  {/* Info du sujet */}
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-slate-200 mb-1 line-clamp-2">
                                      {topic.title}
                                    </h4>
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                                      <span className="flex items-center gap-1">
                                        <ExternalLink className="w-3 h-3" />
                                        {topic.source}
                                      </span>
                                    </div>
                                    
                                    {/* Sélection des sites (si sujet sélectionné) */}
                                    {isSelected && permissions.can_send_feedback && (
                                      <div className="mt-3 p-2 bg-slate-800/50 rounded-lg">
                                        <p className="text-xs text-slate-400 mb-2">Publier sur :</p>
                                        <div className="flex flex-wrap gap-2">
                                          {sites.map(site => (
                                            <button
                                              key={site.id}
                                              onClick={() => toggleTopicSelection(topic, site.id)}
                                              className={`px-2 py-1 text-xs rounded border transition-colors ${
                                                selectedSiteIds.includes(site.id)
                                                  ? 'bg-sky-600 border-sky-500 text-white'
                                                  : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:border-sky-500'
                                              }`}
                                            >
                                              {site.name}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-2 shrink-0">
                                    {permissions.can_send_feedback && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant={localFeedbacks[topic.id] === true ? "default" : "outline"}
                                          className={`h-8 w-8 p-0 ${localFeedbacks[topic.id] === true ? 'bg-green-600' : ''}`}
                                          onClick={() => sendFeedback(topic.id, true)}
                                          title="Utile"
                                        >
                                          <ThumbsUp className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant={localFeedbacks[topic.id] === false ? "default" : "outline"}
                                          className={`h-8 w-8 p-0 ${localFeedbacks[topic.id] === false ? 'bg-red-600' : ''}`}
                                          onClick={() => sendFeedback(topic.id, false)}
                                          title="Pas utile"
                                        >
                                          <ThumbsDown className="w-4 h-4" />
                                        </Button>
                                      </>
                                    )}
                                    
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8"
                                      onClick={() => window.open(topic.url, '_blank')}
                                      title="Voir source"
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Colonne de droite - Sélection & File d'attente */}
          <div className="space-y-4">
            {/* Sélection en cours */}
            {permissions.can_send_feedback && selectedCount > 0 && (
              <Card className="bg-sky-500/10 border-sky-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="w-5 h-5 text-sky-400" />
                    Sélection ({selectedCount} sujets)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-400">
                    {totalSelectedArticles} article(s) à générer
                  </p>
                  
                  {/* Mode de publication */}
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400 font-medium">Mode de publication :</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setPublishMode('manual')}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors flex items-center gap-1 ${
                          publishMode === 'manual'
                            ? 'bg-slate-600 border-slate-500 text-white'
                            : 'border-slate-600 text-slate-400 hover:border-slate-500'
                        }`}
                      >
                        <PenTool className="w-3 h-3" />
                        Manuel
                      </button>
                      <button
                        onClick={() => setPublishMode('night')}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors flex items-center gap-1 ${
                          publishMode === 'night'
                            ? 'bg-indigo-600 border-indigo-500 text-white'
                            : 'border-slate-600 text-slate-400 hover:border-indigo-500'
                        }`}
                      >
                        <Moon className="w-3 h-3" />
                        Nuit (20h-8h)
                      </button>
                      <button
                        onClick={() => setPublishMode('weekend')}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors flex items-center gap-1 ${
                          publishMode === 'weekend'
                            ? 'bg-amber-600 border-amber-500 text-white'
                            : 'border-slate-600 text-slate-400 hover:border-amber-500'
                        }`}
                      >
                        <Sun className="w-3 h-3" />
                        Week-end
                      </button>
                    </div>
                  </div>
                  
                  <Button
                    onClick={addSelectedToQueue}
                    disabled={loading || totalSelectedArticles === 0}
                    className="w-full bg-sky-600 hover:bg-sky-500"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Ajouter à la file
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* File d'attente */}
            <Card className="bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ListTodo className="w-5 h-5 text-amber-400" />
                    File d&apos;attente
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={loadQueue}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats */}
                {queueStats && (
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 bg-slate-800/50 rounded">
                      <div className="text-xl font-bold text-amber-400">{queueStats.pending || 0}</div>
                      <div className="text-xs text-slate-400">En attente</div>
                    </div>
                    <div className="p-2 bg-slate-800/50 rounded">
                      <div className="text-xl font-bold text-blue-400">{queueStats.generating || 0}</div>
                      <div className="text-xs text-slate-400">En cours</div>
                    </div>
                    <div className="p-2 bg-slate-800/50 rounded">
                      <div className="text-xl font-bold text-green-400">{queueStats.published || 0}</div>
                      <div className="text-xs text-slate-400">Publiés</div>
                    </div>
                  </div>
                )}

                {/* Prochaine publication */}
                {queueStats?.next_publication && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
                      <Clock className="w-4 h-4" />
                      Prochaine publication
                    </div>
                    <div className="text-xs text-slate-300">
                      {formatScheduledTime(queueStats.next_publication.scheduled_at)}
                    </div>
                  </div>
                )}

                {/* Liste simplifiée */}
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {queue.filter(q => q.status === 'pending').slice(0, 10).map(item => (
                    <div 
                      key={item.id}
                      className="p-2 bg-slate-800/50 rounded text-sm flex items-center justify-between gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-slate-200">{item.topic_title}</div>
                        <div className="text-xs text-slate-400">
                          {item.site_name} • {formatScheduledTime(item.scheduled_at)}
                        </div>
                      </div>
                      {permissions.can_send_feedback && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                          onClick={() => removeFromQueue(item.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  {queue.filter(q => q.status === 'pending').length === 0 && (
                    <p className="text-center text-slate-500 text-sm py-4">
                      File vide
                    </p>
                  )}
                </div>

                {/* Paramètres automatiques */}
                {permissions.can_send_feedback && (
                  <div className="pt-4 border-t border-slate-700 space-y-3">
                    <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Publication automatique
                    </h4>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Moon className="w-4 h-4 text-indigo-400" />
                        <span className="text-sm text-slate-400">Mode nuit</span>
                      </div>
                      <Switch
                        checked={queueSettings.night_mode_enabled}
                        onCheckedChange={(v) => updateSettings('night_mode_enabled', v)}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sun className="w-4 h-4 text-amber-400" />
                        <span className="text-sm text-slate-400">Week-end / Fériés</span>
                      </div>
                      <Switch
                        checked={queueSettings.weekend_mode_enabled}
                        onCheckedChange={(v) => updateSettings('weekend_mode_enabled', v)}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-sky-400" />
                        <span className="text-sm text-slate-400">Traitement auto</span>
                      </div>
                      <Switch
                        checked={queueSettings.auto_process_enabled}
                        onCheckedChange={(v) => updateSettings('auto_process_enabled', v)}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info admin */}
            {permissions.can_send_feedback && (
              <Card className="bg-amber-500/10 border-amber-500/30">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-amber-400 font-medium">Mode TEST</p>
                      <p className="text-amber-300/70 mt-1 text-xs">
                        Pas de publication entre 01h et 05h. 
                        Écart minimum de 1h entre publications.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoutMedia;
